import React, { createContext, useCallback, useEffect } from "react";
import { getCookie } from "../utils/api";
import { useSnackbar } from "notistack";
import { useLocation, useNavigate } from "react-router-dom";
import { useLoginPopup } from "./LoginPopupContext";
import envConfig from "../config";
import { isPlaceholderApiKeyExpiration } from "../utils/api-key-expiry";
import {
  dispatchSessionRefreshed,
  refreshAuthxSessionCoalesced,
} from "../utils/authx-session-refresh";
import { hasSessionKeyCookies } from "../utils/session-reauth";

const AuthContext = createContext();

const GLOBAL_SESSION_REFRESH_INTERVAL_MS = 60 * 1000;

const AuthProvider = ({ children }) => {
  const { handleOpen } = useLoginPopup();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  const runGlobalSessionRefresh = useCallback(() => {
    if (process.env.NODE_ENV === "development") {
      return;
    }
    if (!hasSessionKeyCookies()) {
      return;
    }
    void refreshAuthxSessionCoalesced().then((ok) => {
      if (ok) {
        dispatchSessionRefreshed();
      }
    });
  }, []);

  const checkAuth = () => {
    const path = location.pathname;
    const storedTime = getCookie("apiKeyExpirationUTCDate");
    const accessKeyCookie = getCookie("accessKey");
    const refreshKeyCookie = getCookie("refreshKey");

    if (
      isPlaceholderApiKeyExpiration(storedTime) ||
      `${storedTime}` === "null" ||
      !storedTime
    ) {
      if (
        !accessKeyCookie ||
        accessKeyCookie === "undefined" ||
        !refreshKeyCookie ||
        refreshKeyCookie === "undefined"
      ) {
        window.location = `https://${envConfig.loginUrl}/`;
        return;
      }
      return;
    }

    const currentTimeUTC = new Date().toUTCString();
    const storedTimeUTC = new Date(storedTime + "Z").toUTCString();
    const difference =
      new Date(storedTimeUTC).getTime() - new Date(currentTimeUTC).getTime();

    const maxDiff = envConfig.loginUrl.includes(".app") ? 1 : 5;
    if (difference > maxDiff * 60 * 1000) return;

    if (
      difference <= maxDiff * 60 * 1000 &&
      accessKeyCookie &&
      accessKeyCookie !== "undefined" &&
      refreshKeyCookie &&
      refreshKeyCookie !== "undefined"
    ) {
      if (path === "/mfa") {
        enqueueSnackbar("Session expired, please log in again.", {
          variant: "info",
        });
        navigate("/");
        return;
      }
      void refreshAuthxSessionCoalesced().then((ok) => {
        if (ok) {
          dispatchSessionRefreshed();
          return;
        }
        handleOpen();
      });
    } else {
      window.location = `https://${envConfig.loginUrl}/`;
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const path = location.pathname;

      if (
        path === "" ||
        path === "/" ||
        path === "/register" ||
        path === "/verify-email" ||
        path === "/verify-phone" ||
        path === "/mfa" ||
        path === "/reset-password" ||
        path.includes("bank-feeds-connected") ||
        path.includes("/forgotuserpassword") ||
        path.includes("dev/test-errors") ||
        path.includes("dev/new-login-test")
      ) {
        return;
      }

      checkAuth();
    }, 5000);

    return () => clearInterval(interval);
  }, [location]);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      return undefined;
    }
    runGlobalSessionRefresh();
    const id = setInterval(
      runGlobalSessionRefresh,
      GLOBAL_SESSION_REFRESH_INTERVAL_MS,
    );
    const onWake = () => {
      if (document.visibilityState === "visible") {
        runGlobalSessionRefresh();
      }
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [runGlobalSessionRefresh]);

  return <AuthContext.Provider value={{}}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };
